// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Mål afstand / Mål areal on the Ground (frontend-aa2): the measurements'
 * geometry (polyline length, polygon area by the shoelace formula in the
 * plan, on the best-fit plane in 3D), their Danish formatting, the editing
 * state (a draft being clicked, the finished ones kept until "Ryd
 * målinger") and its per-case session storage. Pure: no React, no DOM, no
 * three.js — the overlay (`routes/bygning/MeasureLayer.tsx`) draws it.
 *
 * Not to be confused with `measure.ts`: "Mål fra modellen" (the server's
 * per-part quantities). These measurements never leave the browser.
 */

/** A world point, metres. */
export type Vec3 = readonly [number, number, number];

export type MeasureKind = 'distance' | 'area';

export interface Measurement {
  id: string;
  kind: MeasureKind;
  /** World points in click order; an area's ring is open (the last does not repeat the first). */
  points: Vec3[];
  /**
   * Measured in the locked plan: lengths and area are taken in x/y only
   * (the points sit at the storey's floor). Otherwise true 3D.
   */
  plan: boolean;
}

// --------------------------------------------------------------- geometry --

/** The distance between two points: in x/y only when `plan`, else in 3D. */
export function distance(a: Vec3, b: Vec3, plan: boolean): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = plan ? 0 : b[2] - a[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Each segment's length, in order; `closed` adds the one back to the first point. */
export function segmentLengths(points: readonly Vec3[], plan: boolean, closed = false): number[] {
  const out: number[] = [];
  for (let i = 1; i < points.length; i += 1) out.push(distance(points[i - 1], points[i], plan));
  if (closed && points.length > 2) out.push(distance(points[points.length - 1], points[0], plan));
  return out;
}

/** The polyline's length (open), or the ring's perimeter (`closed`). */
export function polylineLength(points: readonly Vec3[], plan: boolean, closed = false): number {
  return segmentLengths(points, plan, closed).reduce((sum, l) => sum + l, 0);
}

/** The area of the x/y polygon (shoelace), always ≥ 0; under three points 0. */
export function shoelaceArea(points: readonly (readonly [number, number] | Vec3)[]): number {
  if (points.length < 3) return 0;
  // Relative to the first point: world coordinates are large (UTM) and the
  // products would lose the centimetres otherwise.
  const ox = points[0][0];
  const oy = points[0][1];
  let twice = 0;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    twice += (p[0] - ox) * (q[1] - oy) - (q[0] - ox) * (p[1] - oy);
  }
  return Math.abs(twice) / 2;
}

/**
 * Newell's normal of a (possibly not quite planar) ring: its direction is
 * the best-fit plane's normal and its length twice the area of the ring
 * projected onto that plane. Computed about the centroid for precision.
 */
export function newellNormal(points: readonly Vec3[]): Vec3 {
  const c = centroid(points);
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const px = p[0] - c[0];
    const py = p[1] - c[1];
    const pz = p[2] - c[2];
    const qx = q[0] - c[0];
    const qy = q[1] - c[1];
    const qz = q[2] - c[2];
    nx += (py - qy) * (pz + qz);
    ny += (pz - qz) * (px + qx);
    nz += (px - qx) * (py + qy);
  }
  return [nx, ny, nz];
}

/** The mean of the points; [0, 0, 0] for none. */
export function centroid(points: readonly Vec3[]): Vec3 {
  if (points.length === 0) return [0, 0, 0];
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    x += p[0];
    y += p[1];
    z += p[2];
  }
  return [x / points.length, y / points.length, z / points.length];
}

export interface FitPlane {
  /** A point on the plane (the centroid). */
  origin: Vec3;
  /** Unit normal, pointing up (nz ≥ 0) so the tilt reads the same either winding. */
  normal: Vec3;
  /** The ring's area projected onto the plane, m². */
  area: number;
  /** The angle between the plane and the horizontal, degrees: 0 a floor, 90 a wall. */
  tiltDeg: number;
  /** The farthest any point lies off the plane, metres. */
  deviation: number;
}

/**
 * The best-fit plane of a ring in 3D (Newell; frontend-aa2's "project onto
 * the best-fit plane"), with the area projected onto it and its tilt. Null
 * when the ring has no area (under three points, or all in a line).
 */
export function bestFitPlane(points: readonly Vec3[]): FitPlane | null {
  if (points.length < 3) return null;
  const n = newellNormal(points);
  const len = Math.hypot(n[0], n[1], n[2]);
  if (!(len > 1e-12)) return null;
  const sign = n[2] < 0 ? -1 : 1;
  const normal: Vec3 = [(sign * n[0]) / len, (sign * n[1]) / len, (sign * n[2]) / len];
  const origin = centroid(points);
  let deviation = 0;
  for (const p of points) {
    const d = Math.abs((p[0] - origin[0]) * normal[0] + (p[1] - origin[1]) * normal[1] + (p[2] - origin[2]) * normal[2]);
    deviation = Math.max(deviation, d);
  }
  const tiltDeg = (Math.acos(Math.min(1, Math.abs(normal[2]))) * 180) / Math.PI;
  return { origin, normal, area: len / 2, tiltDeg, deviation };
}

/** What a measurement amounts to, ready to label. */
export interface MeasureSummary {
  /** Each segment's length (an area's ring includes the closing one). */
  segments: number[];
  /** A distance's total length; an area's perimeter. */
  length: number;
  /** m²; null for a distance, or an area with under three points. */
  area: number | null;
  /** 3D areas only: the plane's tilt from horizontal, degrees. */
  tiltDeg: number | null;
  /** 3D areas only: how far the farthest point lies off the best-fit plane, metres. */
  deviation: number | null;
  /** An area whose outline crosses itself: `area` is null, there is no meaningful one. */
  selfIntersecting: boolean;
  /** What to tell the user about the value, if anything (Danish). */
  warning: string | null;
}

/** Points farther than this off the best-fit plane (metres) get a warning. */
export const PLANE_DEVIATION_WARN = 0.05;

/** Whether segments p1–p2 and q1–q2 properly cross (touching ends do not count). */
function segmentsCross(p1: readonly number[], p2: readonly number[], q1: readonly number[], q2: readonly number[]): boolean {
  const orient = (a: readonly number[], b: readonly number[], c: readonly number[]) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = orient(q1, q2, p1);
  const d2 = orient(q1, q2, p2);
  const d3 = orient(p1, p2, q1);
  const d4 = orient(p1, p2, q2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** Whether a closed ring (2D, open: the last point joins the first) crosses itself. */
export function ringSelfIntersects(ring: readonly (readonly number[])[]): boolean {
  const n = ring.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i += 1) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 2; j < n; j += 1) {
      // Neighbours share a point; the last edge neighbours the first.
      if (i === 0 && j === n - 1) continue;
      if (segmentsCross(a1, a2, ring[j], ring[(j + 1) % n])) return true;
    }
  }
  return false;
}

/** The unit normal of the widest pair of spokes from the centroid; null when the points lie in a line. */
function spanNormal(points: readonly Vec3[]): Vec3 | null {
  const c = centroid(points);
  let best: Vec3 | null = null;
  let bestLen = 1e-12;
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const a = [points[i][0] - c[0], points[i][1] - c[1], points[i][2] - c[2]];
      const b = [points[j][0] - c[0], points[j][1] - c[1], points[j][2] - c[2]];
      const n: Vec3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
      const len = Math.hypot(n[0], n[1], n[2]);
      if (len > bestLen) {
        bestLen = len;
        best = [n[0] / len, n[1] / len, n[2] / len];
      }
    }
  }
  return best;
}

/** A 3D ring in the 2D coordinates of a plane through `origin` with unit `normal`. */
function onPlane(points: readonly Vec3[], origin: Vec3, normal: Vec3): [number, number][] {
  // Any unit vector across the normal, then the third by the cross product.
  const helper: Vec3 = Math.abs(normal[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  let u: Vec3 = [helper[1] * normal[2] - helper[2] * normal[1], helper[2] * normal[0] - helper[0] * normal[2], helper[0] * normal[1] - helper[1] * normal[0]];
  const lu = Math.hypot(u[0], u[1], u[2]);
  u = [u[0] / lu, u[1] / lu, u[2] / lu];
  const v: Vec3 = [normal[1] * u[2] - normal[2] * u[1], normal[2] * u[0] - normal[0] * u[2], normal[0] * u[1] - normal[1] * u[0]];
  return points.map((p) => {
    const r = [p[0] - origin[0], p[1] - origin[1], p[2] - origin[2]];
    return [r[0] * u[0] + r[1] * u[1] + r[2] * u[2], r[0] * v[0] + r[1] * v[1] + r[2] * v[2]];
  });
}

export function summarize(m: Pick<Measurement, 'kind' | 'points' | 'plan'>): MeasureSummary {
  const closed = m.kind === 'area';
  const segments = segmentLengths(m.points, m.plan, closed);
  const length = segments.reduce((s, l) => s + l, 0);
  const none = { area: null, tiltDeg: null, deviation: null, selfIntersecting: false, warning: null };
  if (m.kind === 'distance' || m.points.length < 3) return { segments, length, ...none };
  const crossed = 'flagen krydser sig selv — intet gyldigt areal';
  if (m.plan) {
    const ring = m.points.map((p) => [p[0] - m.points[0][0], p[1] - m.points[0][1]]);
    if (ringSelfIntersects(ring)) return { segments, length, ...none, selfIntersecting: true, warning: crossed };
    return { segments, length, ...none, area: shoelaceArea(m.points) };
  }
  const fit = bestFitPlane(m.points);
  if (!fit) {
    // No net area (Newell's normal vanishes): a bow-tie whose lobes cancel, or points in a line.
    const normal = spanNormal(m.points);
    if (normal && ringSelfIntersects(onPlane(m.points, centroid(m.points), normal))) {
      return { segments, length, ...none, selfIntersecting: true, warning: crossed };
    }
    return { segments, length, ...none, area: 0 };
  }
  if (ringSelfIntersects(onPlane(m.points, fit.origin, fit.normal))) {
    return { segments, length, ...none, tiltDeg: fit.tiltDeg, deviation: fit.deviation, selfIntersecting: true, warning: crossed };
  }
  const warning =
    fit.deviation > PLANE_DEVIATION_WARN ? `punkterne ligger ikke i ét plan (afvigelse ${Math.round(fit.deviation * 100)} cm)` : null;
  return { segments, length, area: fit.area, tiltDeg: fit.tiltDeg, deviation: fit.deviation, selfIntersecting: false, warning };
}

// ------------------------------------------------------------- formatting --

/** `n` to `digits` decimals, Danish; a value that rounds to zero reads "0,00", never "-0,00". */
const fixed = (n: number, digits: number) => {
  const r = Number(n.toFixed(digits));
  return (r === 0 ? 0 : r).toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/** A length in metres, to the centimetre: "3,42 m", "1.204,50 m". */
export function formatLength(m: number): string {
  return `${fixed(m, 2)} m`;
}

/** An area: "12,6 m²" from 10 m² up, "4,25 m²" below (a door's area needs the second decimal). */
export function formatArea(m2: number): string {
  return `${fixed(m2, m2 >= 10 ? 1 : 2)} m²`;
}

/** An angle: "32°", "4,5°" below ten. */
export function formatAngle(deg: number): string {
  return `${fixed(deg, deg >= 10 ? 0 : 1)}°`;
}

/** The value "Kopiér" puts on the clipboard. */
export function copyText(m: Pick<Measurement, 'kind' | 'points' | 'plan'>): string {
  const s = summarize(m);
  if (m.kind === 'distance') return formatLength(s.length);
  if (s.selfIntersecting) return s.warning ?? '';
  const parts = [formatArea(s.area ?? 0), `omkreds ${formatLength(s.length)}`];
  if (s.tiltDeg !== null) parts.push(`hældning ${formatAngle(s.tiltDeg)}`);
  if (s.warning) parts.push(s.warning);
  return parts.join(', ');
}

/** The measurement's headline: the total length, or the area. */
export function headline(m: Pick<Measurement, 'kind' | 'points' | 'plan'>): string {
  const s = summarize(m);
  if (m.kind === 'distance') return formatLength(s.length);
  return s.selfIntersecting ? 'ugyldigt areal' : formatArea(s.area ?? 0);
}

// ------------------------------------------------------------------ state --

/** The tool on: which kind the next clicks draw. */
export type MeasureTool = MeasureKind | null;

export interface MeasureState {
  tool: MeasureTool;
  /** Finished measurements, oldest first. */
  items: Measurement[];
  /** The one being clicked, if any. */
  draft: Measurement | null;
  /** The next id's number (ids stay unique across a session). */
  seq: number;
}

export const EMPTY_MEASURE: MeasureState = { tool: null, items: [], draft: null, seq: 1 };

export type MeasureAction =
  | { type: 'tool'; tool: MeasureTool }
  /** A click: start a draft with the tool, or add to it. */
  | { type: 'add'; point: Vec3; plan: boolean }
  /** Double-click / Enter: keep the draft if it measures something. */
  | { type: 'finish' }
  /** Backspace: the draft's last point (the draft goes with its first). */
  | { type: 'undo' }
  /** Esc: drop the draft. */
  | { type: 'cancel' }
  /** A vertex dragged: of a finished measurement or the draft. */
  | { type: 'move'; id: string; index: number; point: Vec3 }
  | { type: 'remove'; id: string }
  | { type: 'clear' }
  /** Another case opened: its stored state (`restoreMeasurements`). */
  | { type: 'load'; state: MeasureState };

/** Points a finished measurement needs: two for a distance, three for an area. */
export function minPoints(kind: MeasureKind): number {
  return kind === 'distance' ? 2 : 3;
}

/** Two clicks this close (metres; both coordinates) are one point clicked twice. */
const SAME_POINT = 1e-6;

const samePoint = (a: Vec3, b: Vec3) =>
  Math.abs(a[0] - b[0]) < SAME_POINT && Math.abs(a[1] - b[1]) < SAME_POINT && Math.abs(a[2] - b[2]) < SAME_POINT;

function finishDraft(state: MeasureState): MeasureState {
  const d = state.draft;
  if (!d) return state;
  if (d.points.length < minPoints(d.kind)) return { ...state, draft: null };
  return { ...state, items: [...state.items, d], draft: null };
}

export function measureReducer(state: MeasureState, action: MeasureAction): MeasureState {
  switch (action.type) {
    case 'tool': {
      if (action.tool === state.tool) return state;
      // Switching tools keeps a draft that measures something.
      return { ...finishDraft(state), tool: action.tool };
    }
    case 'add': {
      if (!state.tool) return state;
      const d = state.draft;
      // Another tool, or another view (plan points are flat, 3D ones are not): a new measurement.
      if (!d || d.kind !== state.tool || d.plan !== action.plan) {
        const base = d ? finishDraft(state) : state;
        return {
          ...base,
          draft: { id: `m${base.seq}`, kind: state.tool, points: [action.point], plan: action.plan },
          seq: base.seq + 1,
        };
      }
      // A second click on the same spot (the double-click's) adds nothing.
      if (d.points.length > 0 && samePoint(d.points[d.points.length - 1], action.point)) return state;
      return { ...state, draft: { ...d, points: [...d.points, action.point] } };
    }
    case 'finish':
      return finishDraft(state);
    case 'undo': {
      const d = state.draft;
      if (!d) return state;
      if (d.points.length <= 1) return { ...state, draft: null };
      return { ...state, draft: { ...d, points: d.points.slice(0, -1) } };
    }
    case 'cancel':
      return state.draft ? { ...state, draft: null } : state;
    case 'move': {
      const edit = (m: Measurement): Measurement => {
        if (m.id !== action.id || action.index < 0 || action.index >= m.points.length) return m;
        const points = m.points.slice();
        points[action.index] = action.point;
        return { ...m, points };
      };
      if (state.draft?.id === action.id) return { ...state, draft: edit(state.draft) };
      return { ...state, items: state.items.map(edit) };
    }
    case 'remove':
      return {
        ...state,
        items: state.items.filter((m) => m.id !== action.id),
        draft: state.draft?.id === action.id ? null : state.draft,
      };
    case 'clear':
      return { ...state, items: [], draft: null };
    case 'load':
      return action.state;
  }
}

// ---------------------------------------------------------------- storage --

export interface MeasureStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const isVec3 = (v: unknown): v is Vec3 =>
  Array.isArray(v) && v.length === 3 && v.every((c) => typeof c === 'number' && Number.isFinite(c));

/** The finished measurements in a stored string; anything garbled is left out. */
export function parseMeasurements(raw: string | null): Measurement[] {
  if (!raw) return [];
  let list: unknown;
  try {
    list = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(list)) return [];
  const out: Measurement[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const m = item as Record<string, unknown>;
    if (typeof m.id !== 'string' || (m.kind !== 'distance' && m.kind !== 'area') || !Array.isArray(m.points)) continue;
    const points = m.points.filter(isVec3);
    if (points.length !== m.points.length || points.length < minPoints(m.kind)) continue;
    out.push({ id: m.id, kind: m.kind, points: points.map((p) => [p[0], p[1], p[2]] as Vec3), plan: m.plan === true });
  }
  return out;
}

/**
 * The state to start a case's session with: its stored measurements, the
 * tool off. `key` is the user- and case-scoped storage key
 * (`settingsStorage.measureKey`), so signing out clears it with the rest.
 */
export function restoreMeasurements(key: string, storage: MeasureStorage | undefined): MeasureState {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(key) ?? null;
  } catch {
    raw = null;
  }
  const items = parseMeasurements(raw);
  const seq = items.reduce((max, m) => Math.max(max, Number(/^m(\d+)$/.exec(m.id)?.[1] ?? 0)), 0) + 1;
  return { ...EMPTY_MEASURE, items, seq };
}

export function storeMeasurements(key: string, items: readonly Measurement[], storage: MeasureStorage | undefined): void {
  try {
    if (items.length === 0) storage?.removeItem?.(key);
    else storage?.setItem(key, JSON.stringify(items));
  } catch {
    // A refused write only means the measurements are not kept past a reload.
  }
}
